import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import type { Database } from '@/types/database.types';

type TableRow<TableName extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][TableName]['Row'];

export type ProjectReportFloorPlan = Pick<TableRow<'floor_plans'>, 'id' | 'name' | 'scale_data'>;

type FixtureRow = TableRow<'lighting_fixtures'>;
type ControlRow = TableRow<'lighting_controls'>;
type LvProductRow = TableRow<'lv_products'>;
type RoomRow = TableRow<'rooms'>;
type CircuitRow = TableRow<'circuits'>;
type CablePathRow = TableRow<'cable_paths'>;
type CableJunctionRow = TableRow<'cable_junctions'>;
type AnnotationRow = TableRow<'floor_plan_annotations'>;
type ElevationRow = TableRow<'elevations'>;
type ProductRow = Pick<
  TableRow<'product_library'>,
  | 'id'
  | 'brand'
  | 'model'
  | 'part_number'
  | 'category'
  | 'subcategory'
  | 'wattage'
  | 'power_consumption'
>;

export interface ProjectReportFilters {
  floorPlanId: string | 'all';
}

export interface ProjectReportSummary {
  floorPlans: number;
  rooms: number;
  fixtures: number;
  stripLights: number;
  controls: number;
  lvProducts: number;
  loads: number;
  cablePaths: number;
  junctions: number;
  annotations: number;
  elevations: number;
  unassignedFixtures: number;
  unassignedControls: number;
}

export interface FloorPlanReportRow {
  id: string;
  name: string;
  hasScale: boolean;
  rooms: number;
  fixtures: number;
  stripLights: number;
  controls: number;
  lvProducts: number;
  loads: number;
  cablePaths: number;
  junctions: number;
  annotations: number;
  elevations: number;
}

export interface DeviceScheduleRow {
  id: string;
  type: string;
  name: string;
  category: string;
  productName: string;
  floorPlanName: string;
  roomName: string;
  loadName: string;
  status: string;
}

export interface LoadReportRow {
  id: string;
  loadNumber: string;
  name: string;
  floorPlanName: string;
  fixtureCount: number;
  controlCount: number;
  notes: string;
}

export interface RoomReportRow {
  id: string;
  floorPlanName: string;
  roomName: string;
  roomType: string;
  fixtures: number;
  controls: number;
  lvProducts: number;
}

export interface QaReportItem {
  id: string;
  severity: 'warning' | 'info';
  area: string;
  message: string;
  floorPlanName: string;
}

export interface ProjectReportData {
  floorPlans: ProjectReportFloorPlan[];
  selectedFloorPlans: ProjectReportFloorPlan[];
  summary: ProjectReportSummary;
  floorPlanRows: FloorPlanReportRow[];
  deviceRows: DeviceScheduleRow[];
  loadRows: LoadReportRow[];
  roomRows: RoomReportRow[];
  qaItems: QaReportItem[];
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isStripFixture(fixture: FixtureRow): boolean {
  return fixture.fixture_type.toLowerCase().includes('strip');
}

function titleCase(value: string | null | undefined): string {
  if (!value) return 'Unspecified';
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase());
}

function productName(product: ProductRow | undefined): string {
  if (!product) return 'No product linked';
  return [product.brand, product.model || product.part_number].filter(Boolean).join(' • ');
}

function createEmptyReportData(
  floorPlans: ProjectReportFloorPlan[],
  selectedFloorPlans: ProjectReportFloorPlan[],
): ProjectReportData {
  return {
    floorPlans,
    selectedFloorPlans,
    summary: {
      floorPlans: selectedFloorPlans.length,
      rooms: 0,
      fixtures: 0,
      stripLights: 0,
      controls: 0,
      lvProducts: 0,
      loads: 0,
      cablePaths: 0,
      junctions: 0,
      annotations: 0,
      elevations: 0,
      unassignedFixtures: 0,
      unassignedControls: 0,
    },
    floorPlanRows: selectedFloorPlans.map(floorPlan => ({
      id: floorPlan.id,
      name: floorPlan.name,
      hasScale: Boolean(floorPlan.scale_data),
      rooms: 0,
      fixtures: 0,
      stripLights: 0,
      controls: 0,
      lvProducts: 0,
      loads: 0,
      cablePaths: 0,
      junctions: 0,
      annotations: 0,
      elevations: 0,
    })),
    deviceRows: [],
    loadRows: [],
    roomRows: [],
    qaItems: [],
  };
}

async function fetchProjectReportData(
  projectId: string,
  filters: ProjectReportFilters,
): Promise<ProjectReportData> {
  const supabase = await getAuthenticatedSupabaseClient();

  const { data: floorPlansData, error: floorPlansError } = await supabase
    .from('floor_plans')
    .select('id, name, scale_data')
    .eq('project_id', projectId)
    .order('name');

  if (floorPlansError) throw floorPlansError;

  const floorPlans = (floorPlansData ?? []) as ProjectReportFloorPlan[];
  const selectedFloorPlans =
    filters.floorPlanId === 'all'
      ? floorPlans
      : floorPlans.filter(floorPlan => floorPlan.id === filters.floorPlanId);
  const selectedFloorPlanIds = selectedFloorPlans.map(floorPlan => floorPlan.id);

  if (selectedFloorPlanIds.length === 0) {
    return createEmptyReportData(floorPlans, selectedFloorPlans);
  }

  const [
    fixturesResult,
    controlsResult,
    lvProductsResult,
    roomsResult,
    circuitsResult,
    cablePathsResult,
    junctionsResult,
    annotationsResult,
    elevationsResult,
  ] = await Promise.all([
    supabase.from('lighting_fixtures').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('lighting_controls').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('lv_products').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('rooms').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('circuits').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('cable_paths').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('cable_junctions').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('floor_plan_annotations').select('*').in('floor_plan_id', selectedFloorPlanIds),
    supabase.from('elevations').select('*').in('floor_plan_id', selectedFloorPlanIds),
  ]);

  if (fixturesResult.error) throw fixturesResult.error;
  if (controlsResult.error) throw controlsResult.error;
  if (lvProductsResult.error) throw lvProductsResult.error;
  if (roomsResult.error) throw roomsResult.error;
  if (circuitsResult.error) throw circuitsResult.error;
  if (cablePathsResult.error) throw cablePathsResult.error;
  if (junctionsResult.error) throw junctionsResult.error;
  if (annotationsResult.error) throw annotationsResult.error;
  if (elevationsResult.error) throw elevationsResult.error;

  const fixtures = (fixturesResult.data ?? []) as FixtureRow[];
  const controls = (controlsResult.data ?? []) as ControlRow[];
  const lvProducts = (lvProductsResult.data ?? []) as LvProductRow[];
  const rooms = (roomsResult.data ?? []) as RoomRow[];
  const circuits = (circuitsResult.data ?? []) as CircuitRow[];
  const cablePaths = (cablePathsResult.data ?? []) as CablePathRow[];
  const junctions = (junctionsResult.data ?? []) as CableJunctionRow[];
  const annotations = (annotationsResult.data ?? []) as AnnotationRow[];
  const elevations = (elevationsResult.data ?? []) as ElevationRow[];

  const productIds = Array.from(
    new Set([
      ...fixtures.map(fixture => fixture.product_id).filter(isNonEmptyString),
      ...controls.map(control => control.product_id).filter(isNonEmptyString),
      ...lvProducts.map(product => product.product_id).filter(isNonEmptyString),
      ...junctions.map(junction => junction.product_id).filter(isNonEmptyString),
    ]),
  );

  let products: ProductRow[] = [];
  if (productIds.length > 0) {
    const { data: productsData, error: productsError } = await supabase
      .from('product_library')
      .select('id, brand, model, part_number, category, subcategory, wattage, power_consumption')
      .in('id', productIds);

    if (productsError) throw productsError;
    products = (productsData ?? []) as ProductRow[];
  }

  const floorPlanNameById = new Map(
    selectedFloorPlans.map(floorPlan => [floorPlan.id, floorPlan.name]),
  );
  const roomNameById = new Map(rooms.map(room => [room.id, room.name]));
  const circuitById = new Map(circuits.map(circuit => [circuit.id, circuit]));
  const productById = new Map(products.map(product => [product.id, product]));
  const connectedJunctionIds = new Set(
    cablePaths.flatMap(path =>
      [path.start_junction_id, path.end_junction_id].filter(isNonEmptyString),
    ),
  );

  const fixtureDevices: DeviceScheduleRow[] = fixtures.map(fixture => {
    const product = fixture.product_id ? productById.get(fixture.product_id) : undefined;
    const circuit = fixture.circuit_id ? circuitById.get(fixture.circuit_id) : undefined;
    const status = [
      !fixture.product_id ? 'No product' : null,
      !fixture.circuit_id && !isStripFixture(fixture) ? 'No load' : null,
      !fixture.room_id ? 'No room' : null,
    ].filter(isNonEmptyString);

    return {
      id: fixture.id,
      type: isStripFixture(fixture) ? 'LED Strip' : 'Fixture',
      name: fixture.custom_name || productName(product) || titleCase(fixture.fixture_type),
      category: titleCase(fixture.subcategory || fixture.category || fixture.fixture_type),
      productName: productName(product),
      floorPlanName: floorPlanNameById.get(fixture.floor_plan_id) ?? 'Unknown',
      roomName: fixture.room_id
        ? (roomNameById.get(fixture.room_id) ?? 'Unknown room')
        : 'Unassigned',
      loadName: circuit
        ? `${circuit.sequence_number ?? '-'} • ${circuit.name}`
        : isStripFixture(fixture)
          ? 'N/A'
          : 'Unassigned',
      status: status.length > 0 ? status.join(', ') : 'Ready',
    };
  });

  const controlDevices: DeviceScheduleRow[] = controls.map(control => {
    const product = control.product_id ? productById.get(control.product_id) : undefined;
    const circuit = control.circuit_id ? circuitById.get(control.circuit_id) : undefined;
    const status = [
      !control.product_id ? 'No product' : null,
      !control.circuit_id && !control.assigned_load ? 'No load' : null,
      !control.room_id ? 'No room' : null,
    ].filter(isNonEmptyString);

    return {
      id: control.id,
      type: 'Control',
      name: control.custom_name || control.name || titleCase(control.type),
      category: titleCase(control.type),
      productName: productName(product),
      floorPlanName: control.floor_plan_id
        ? (floorPlanNameById.get(control.floor_plan_id) ?? 'Unknown')
        : 'Unknown',
      roomName: control.room_id
        ? (roomNameById.get(control.room_id) ?? 'Unknown room')
        : 'Unassigned',
      loadName: circuit
        ? `${circuit.sequence_number ?? '-'} • ${circuit.name}`
        : (control.assigned_load ?? 'Unassigned'),
      status: status.length > 0 ? status.join(', ') : 'Ready',
    };
  });

  const lvProductDevices: DeviceScheduleRow[] = lvProducts.map(lvProduct => {
    const product = lvProduct.product_id ? productById.get(lvProduct.product_id) : undefined;
    const status = [
      !lvProduct.product_id ? 'No product' : null,
      !lvProduct.room_id ? 'No room' : null,
    ].filter(isNonEmptyString);

    return {
      id: lvProduct.id,
      type: 'LV Product',
      name: lvProduct.name || productName(product),
      category: titleCase(lvProduct.subcategory || lvProduct.category),
      productName: productName(product),
      floorPlanName: lvProduct.floor_plan_id
        ? (floorPlanNameById.get(lvProduct.floor_plan_id) ?? 'Unknown')
        : 'Unknown',
      roomName: lvProduct.room_id
        ? (roomNameById.get(lvProduct.room_id) ?? 'Unknown room')
        : 'Unassigned',
      loadName: 'N/A',
      status: status.length > 0 ? status.join(', ') : 'Ready',
    };
  });

  const floorPlanRows: FloorPlanReportRow[] = selectedFloorPlans.map(floorPlan => {
    const floorPlanFixtures = fixtures.filter(fixture => fixture.floor_plan_id === floorPlan.id);
    return {
      id: floorPlan.id,
      name: floorPlan.name,
      hasScale: Boolean(floorPlan.scale_data),
      rooms: rooms.filter(room => room.floor_plan_id === floorPlan.id).length,
      fixtures: floorPlanFixtures.filter(fixture => !isStripFixture(fixture)).length,
      stripLights: floorPlanFixtures.filter(isStripFixture).length,
      controls: controls.filter(control => control.floor_plan_id === floorPlan.id).length,
      lvProducts: lvProducts.filter(product => product.floor_plan_id === floorPlan.id).length,
      loads: circuits.filter(circuit => circuit.floor_plan_id === floorPlan.id).length,
      cablePaths: cablePaths.filter(path => path.floor_plan_id === floorPlan.id).length,
      junctions: junctions.filter(junction => junction.floor_plan_id === floorPlan.id).length,
      annotations: annotations.filter(annotation => annotation.floor_plan_id === floorPlan.id)
        .length,
      elevations: elevations.filter(elevation => elevation.floor_plan_id === floorPlan.id).length,
    };
  });

  const loadRows: LoadReportRow[] = circuits
    .map(circuit => {
      const fixtureCount = fixtures.filter(fixture => fixture.circuit_id === circuit.id).length;
      const controlCount = controls.filter(control => control.circuit_id === circuit.id).length;
      return {
        id: circuit.id,
        loadNumber: circuit.sequence_number?.toString() ?? '-',
        name: circuit.name,
        floorPlanName: floorPlanNameById.get(circuit.floor_plan_id) ?? 'Unknown',
        fixtureCount,
        controlCount,
        notes: circuit.notes ?? '',
      };
    })
    .sort((a, b) => Number(a.loadNumber) - Number(b.loadNumber));

  const roomRows: RoomReportRow[] = rooms.map(room => ({
    id: room.id,
    floorPlanName: floorPlanNameById.get(room.floor_plan_id) ?? 'Unknown',
    roomName: room.name,
    roomType: titleCase(room.room_type || room.class),
    fixtures: fixtures.filter(fixture => fixture.room_id === room.id).length,
    controls: controls.filter(control => control.room_id === room.id).length,
    lvProducts: lvProducts.filter(product => product.room_id === room.id).length,
  }));

  const qaItems: QaReportItem[] = [
    ...selectedFloorPlans
      .filter(floorPlan => !floorPlan.scale_data)
      .map(floorPlan => ({
        id: `scale-${floorPlan.id}`,
        severity: 'warning' as const,
        area: 'Scale',
        message: 'Floor plan has no scale calibration.',
        floorPlanName: floorPlan.name,
      })),
    ...fixtures
      .filter(fixture => !isStripFixture(fixture) && !fixture.circuit_id)
      .map(fixture => ({
        id: `fixture-load-${fixture.id}`,
        severity: 'warning' as const,
        area: 'Loads',
        message: `${fixture.custom_name || titleCase(fixture.fixture_type)} is not assigned to a load.`,
        floorPlanName: floorPlanNameById.get(fixture.floor_plan_id) ?? 'Unknown',
      })),
    ...controls
      .filter(control => !control.circuit_id && !control.assigned_load)
      .map(control => ({
        id: `control-load-${control.id}`,
        severity: 'info' as const,
        area: 'Controls',
        message: `${control.custom_name || control.name} is not assigned to a load.`,
        floorPlanName: control.floor_plan_id
          ? (floorPlanNameById.get(control.floor_plan_id) ?? 'Unknown')
          : 'Unknown',
      })),
    ...cablePaths
      .filter(path => !path.start_junction_id || !path.end_junction_id)
      .map(path => ({
        id: `path-endpoint-${path.id}`,
        severity: 'warning' as const,
        area: 'Cable Paths',
        message: `${path.name || 'Cable path'} is missing a start or end junction.`,
        floorPlanName: floorPlanNameById.get(path.floor_plan_id) ?? 'Unknown',
      })),
    ...junctions
      .filter(junction => !connectedJunctionIds.has(junction.id))
      .map(junction => ({
        id: `junction-unused-${junction.id}`,
        severity: 'info' as const,
        area: 'Cable Junctions',
        message: `${junction.name} is not connected to any cable path.`,
        floorPlanName: floorPlanNameById.get(junction.floor_plan_id) ?? 'Unknown',
      })),
  ];

  return {
    floorPlans,
    selectedFloorPlans,
    summary: {
      floorPlans: selectedFloorPlans.length,
      rooms: rooms.length,
      fixtures: fixtures.filter(fixture => !isStripFixture(fixture)).length,
      stripLights: fixtures.filter(isStripFixture).length,
      controls: controls.length,
      lvProducts: lvProducts.length,
      loads: circuits.length,
      cablePaths: cablePaths.length,
      junctions: junctions.length,
      annotations: annotations.length,
      elevations: elevations.length,
      unassignedFixtures: fixtures.filter(
        fixture => !isStripFixture(fixture) && !fixture.circuit_id,
      ).length,
      unassignedControls: controls.filter(control => !control.circuit_id && !control.assigned_load)
        .length,
    },
    floorPlanRows,
    deviceRows: [...fixtureDevices, ...controlDevices, ...lvProductDevices],
    loadRows,
    roomRows,
    qaItems,
  };
}

export function useProjectReportData(projectId: string, filters: ProjectReportFilters) {
  const queryKey = useMemo(
    () => ['project-report-data', projectId, filters.floorPlanId],
    [projectId, filters.floorPlanId],
  );

  return useQuery({
    queryKey,
    queryFn: () => fetchProjectReportData(projectId, filters),
    enabled: Boolean(projectId),
    staleTime: 60 * 1000,
  });
}
